<script lang="ts">
import { page } from '$app/state'
import { CaseStudyCard, WebflowFooter } from '$lib/components/webflow'

const faqs: {
  question: string
  answer: string
  bullets?: { label: string; text: string }[]
  studyLinks?: { label: string; href: string }[]
}[] = [
  {
    question: 'What is IMX Data?',
    answer:
      'IMX Data is one of the largest healthcare claims data providers built specifically for financial firms trading healthcare equities. We deliver over 90 billion de-identified claim events spanning 330+ million patients and 1,500+ healthcare-related tickers with CUSIP mappings, with daily T+1 delivery latency. Our dataset covers 11+ years of history across pharmacy (Rx), medical (Mx), and hospital (Hx) claims, with 80%+ Rx coverage and approximately 70% medical claims coverage.\n\nIMX Data was purpose-built for financial markets. Every aspect of our infrastructure, from pre-built ticker mappings and pre-joined tables to true point-in-time data integrity, is designed for trading strategies, earnings models, and drug adoption analysis. Data is delivered via Snowflake or AWS (US-EAST-1), allowing teams to begin querying within hours.',
  },
  {
    question: 'What is healthcare claims data?',
    answer:
      'Healthcare claims data is the administrative record generated every time a patient receives medical care, has a procedure, or fills a prescription. Pharmacies, physicians, and hospitals submit these records to payers (insurers, pharmacy benefit managers (PBMs), and government programs) for reimbursement. Each record captures what service or drug was provided, when and where, the diagnosis and procedure codes (ICD/CPT), and payment details.\n\nAt scale, billions of these individual transactions form a near-complete picture of real-world healthcare activity across the U.S. population.',
    bullets: [
      {
        label: 'Use cases',
        text: 'Claims data is primarily used to track prescription volumes and drug market share, forecast pharmaceutical revenue and earnings, monitor adoption curves after new drug launches, analyze competitive dynamics between therapies in the same class, and detect shifts in treatment patterns before they appear in financial disclosures.',
      },
      {
        label: 'How the data is sourced',
        text: 'Claims flow through pharmacy switch networks and medical clearinghouses in near real-time, then are adjudicated by payers over 30 to 90 days. Commercial datasets aggregate records from multiple upstream sources, including retail pharmacy chains, PBMs, clearinghouses, and payer feeds, to maximize coverage.',
      },
    ],
  },
  {
    question: 'How does IMX Data differ from other healthcare claims data providers?',
    answer: 'IMX Data stands apart from other providers across several critical dimensions:',
    bullets: [
      {
        label: 'Delivery Speed',
        text: 'IMX delivers data daily with T+1 latency, processing nearly 75% of Rx claims within 4 days. The industry standard is weekly delivery, and some providers operate on monthly or yearly refresh cycles.',
      },
      {
        label: 'Financial-First Design',
        text: 'IMX is purpose-built for financial firms, with pre-built ticker and CUSIP mappings for 1,500+ tickers. Most providers do not include ticker mapping and often do not offer it at all.',
      },
      {
        label: 'Granularity',
        text: 'IMX provides daily, state-level claim breakdowns with pre-joined, intelligently structured tables. Other providers typically deliver fragmented tables requiring months of processing.',
      },
      {
        label: 'Point-in-Time Integrity',
        text: 'IMX includes aggregation date, ingestion date, and service date fields for true point-in-time accuracy.',
      },
      {
        label: 'Coverage',
        text: '80%+ Rx coverage and 70%+ medical claims from multiple upstream sources. Many providers rely on narrower inputs, making them more vulnerable to disruptions.',
      },
    ],
  },
  {
    question: 'What does T+1 healthcare claims delivery mean?',
    answer:
      "T+1 means healthcare claims processed on a given day (T) are available the next business day (T+1). IMX processes nearly 75% of Rx claims within 4 days, among the fastest in the industry.\n\nFor investment teams, daily delivery provides a significant timing advantage. Faster data means earlier visibility into prescription volumes, drug adoption curves, and market share shifts. In earnings-driven investing, even a few days head start can meaningfully impact alpha generation.\n\nThe industry standard is weekly. Some providers operate on monthly or yearly cycles. IMX Data's daily cadence ensures models reflect the most current market picture.",
  },
  {
    question: 'How can financial firms use healthcare claims data for trading?',
    answer:
      'Healthcare claims data is one of the most valuable alternative data sources for buy-side investors focused on pharmaceutical and biotech equities:',
    bullets: [
      {
        label: 'Earnings Forecasting',
        text: 'Claims data reveals real-time prescription or procedure volumes, enabling bottom-up revenue models that often identify earnings beats or misses before consensus adjusts.',
      },
      {
        label: 'Drug Launch Tracking',
        text: "Claims show actual adoption curves, revealing which physicians prescribe, in which geographies, and at what rate. IMX's day-level and state-level granularity tracks launches in near real-time.",
      },
      {
        label: 'Market Share Analysis',
        text: 'Claims directly measure share shifts between competing drugs. Essential for modeling GLP-1, oncology, and immunology dynamics.',
      },
      {
        label: 'Clinical Signal Detection',
        text: 'Shifts in diagnosis codes and treatment patterns signal emerging trends before the market recognizes them.',
      },
    ],
  },
  {
    question: 'What tickers does IMX Data cover?',
    answer:
      'IMX Data covers over 1,500 healthcare-related tickers with pre-built CUSIP mappings, including drug manufacturers in pharma, biotech, and NPIs in the medical space. More than 75% of pharmaceutical data is already mapped to tickers, purpose-built for financial workflows.\n\nTicker mapping connects claim events directly to publicly traded companies and their specific drug products, enabling company-level and product-level revenue models. Most other providers do not include ticker mapping.',
  },
  {
    question: 'Is IMX Data HIPAA compliant? Is it SOC 2 certified?',
    answer:
      'Yes. IMX Data maintains a HIPAA Compliant Dataset. All data is fully de-identified, with no PII or PHI ever included in any dataset delivered to clients.\n\nIMX Data is also SOC 2 certified, meaning our infrastructure and security controls have been independently audited against AICPA Trust Services Criteria. This gives financial firms confidence that IMX meets the rigorous standards required by institutional investors and compliance teams.',
  },
  {
    question: 'What types of claims does IMX Data include?',
    answer: 'IMX Data provides three primary categories of healthcare claims:',
    bullets: [
      {
        label: 'Pharmacy Claims (Rx)',
        text: 'Prescription drug fills at the pharmacy level, with 80%+ Rx coverage across major U.S. retail channels. The foundation for tracking drug revenue, market share, and adoption curves.',
      },
      {
        label: 'Medical Claims (Mx)',
        text: 'Physician visits, outpatient, procedures, drug administrations, patient demographics, and plan details, with approximately 70% coverage.',
      },
      {
        label: 'Hospital Claims (Hx)',
        text: 'Inpatient admissions, surgeries, infusions, and Emergency Department (ED) visits. Visibility into specialty drug administration (J codes).',
      },
    ],
  },
  {
    question: 'How does IMX Data support analysis of specific drugs and procedures?',
    answer:
      'IMX Data has published extensive case studies on GLP-1 drugs and ADC (antibody-drug conjugate) oncology therapies.',
    bullets: [
      {
        label: 'GLP-1 Analysis',
        text: 'IMX tracked a 3,498% increase in GLP-1 claim volume from 2015 to 2025, identified total market spend, and tracked real-time market share shifts. Day-level granularity enabled investors to forecast quarterly revenue with high accuracy.',
      },
      {
        label: 'ADC Oncology Analysis',
        text: 'IMX tracks prescribing patterns, infusion volumes, and geographic adoption for ADC therapies, identifying which drugs are gaining traction before earnings reports confirm the trends.',
      },
    ],
    studyLinks: [
      { label: 'GLP-1 Drug Market Analysis', href: '/studies/glp-1' },
      { label: 'ADC Oncology Analysis', href: '/studies/adc-oncology-revolution' },
    ],
  },
  {
    question: 'What is the difference between open claims and closed claims data?',
    answer: '',
    bullets: [
      {
        label: 'Open Claims (switch/clearinghouse data)',
        text: 'Captured in real-time as claims flow through the payment system. Offers speed. IMX leverages open claims for daily T+1 delivery.',
      },
      {
        label: 'Closed Claims (adjudicated data)',
        text: 'Fully processed by payers. More complete but with 30 to 90 day latency.',
      },
    ],
  },
]

let openFaqIndex = $state<number | null>(null)

function toggleFaq(i: number) {
  openFaqIndex = openFaqIndex === i ? null : i
}

const orgJsonLd = $derived({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'IMX DATA',
  description:
    'Healthcare data for financial markets. Access 100B+ claims, AI-powered insights, and transparent datasets to power investment models and strategy.',
  url: page.url.origin,
  logo: {
    '@type': 'ImageObject',
    url: 'https://www.imxresearch.com/webflow/images/logo-v2-wht.svg',
  },
  slogan: 'Healthcare data for the financial world.',
  sameAs: ['https://www.linkedin.com/company/imx-data/'],
  knowsAbout: [
    'Healthcare Data',
    'Financial Markets',
    'Claims Data',
    'Investment Models',
    'AI-powered Insights',
  ],
})

const orgJsonLdScript = $derived(
  '<script type="application/ld+json">' + JSON.stringify(orgJsonLd) + '<\/script>',
)
</script>

<svelte:head>
  <title>Healthcare Data for Financial Markets | Claims Data &amp; AI Insights | IMX Research</title
  >
  <meta
    name="description"
    content="Healthcare data for financial markets. Access 100B+ claims, AI-powered insights, and transparent datasets to power investment models and strategy."
  />
  <meta
    property="og:title"
    content="Healthcare Data for Financial Markets | Claims Data &amp; AI Insights | IMX Research"
  />
  <meta
    property="og:description"
    content="Healthcare data for financial markets. Access 100B+ claims, AI-powered insights, and transparent datasets to power investment models and strategy."
  />
  <meta property="og:image" content="https://www.imxresearch.com/webflow/images/Graph-img-1.png" />
  <meta
    name="twitter:title"
    content="Healthcare Data for Financial Markets | Claims Data &amp; AI Insights | IMX Research"
  />
  <meta
    name="twitter:description"
    content="Healthcare data for financial markets. Access 100B+ claims, AI-powered insights, and transparent datasets to power investment models and strategy."
  />
  <meta property="og:type" content="website" />
  <meta name="twitter:card" content="summary_large_image" />
  {@html orgJsonLdScript}
</svelte:head>

<div class="w-layout-blockcontainer container---header w-container">
  <section class="heading-section">
    <div class="container-large">
      <h1 data-w-id="05c97b58-b1ac-eabe-afbc-3861dce3b03f" class="home-header-text">
        <strong class="bold-text">Healthcare data for the financial world.</strong>
      </h1>
      <div class="header-layout">
        <div class="header-content">
          <div class="spacer-32"></div>
          <p data-w-id="05c97b58-b1ac-eabe-afbc-3861dce3b041" class="paragraph large">
            <strong class="bold-text"
              >Utilize low-latency, transparent, big data solutions to merge finance, healthcare,
              and AI.</strong
            ><br />
          </p>
          <div class="spacer-32"></div>
          <div class="button-group">
            <a
              data-w-id="05c97b58-b1ac-eabe-afbc-3861dce3b045"
              href="/contact"
              class="button---contrast w-button">Develop your data solution</a
            >
          </div>
        </div>
      </div>
    </div>
  </section>
</div>
<section class="section-home-first-findings">
  <div class="text-block---heading-alt">
    <span class="section-heading-text---2">Financial-first findings you can trust.</span>
  </div>
  <div class="sub-header-text-alt">
    Access unparalleled healthcare datasets to develop your financial models and investment
    strategies.
  </div>
  <div class="w-layout-blockcontainer container---content w-container">
    <div class="w-layout-grid grid---numbers">
      <div class="div-numbers-grid">
        <div class="numbers-block"><span class="counter">90</span>B+</div>
        <div class="numbers-desc">Total Claim Events</div>
      </div>
      <div class="div-numbers-grid">
        <div class="numbers-block"><span class="counter">330</span>M+</div>
        <div class="numbers-desc">Unique Medical &amp; Pharma Patients</div>
      </div>
      <div class="div-numbers-grid">
        <div class="numbers-block"><span class="counter">500</span>K+</div>
        <div class="numbers-desc"><strong>Total Number of Unique Organizations</strong></div>
      </div>
      <div class="div-numbers-grid">
        <div class="numbers-block"><span class="counter">2.7</span>M+</div>
        <div class="numbers-desc"><strong>Total Number of Unique Providers</strong></div>
      </div>
    </div>
    <div class="w-layout-grid grid---numbers">
      <div class="div-numbers-grid">
        <div class="numbers-block"><span class="counter">90</span>K+</div>
        <div class="numbers-desc"><strong>Total Number of Unique Diagnosis</strong></div>
      </div>
      <div class="div-numbers-grid">
        <div class="numbers-block"><span class="counter">1.5</span>K+</div>
        <div class="numbers-desc"><strong>Medical &amp; Pharma Tickers Mapped</strong></div>
      </div>
      <div class="div-numbers-grid">
        <div class="numbers-block">
          <span class="counter"><strong>4</strong></span><strong>K+</strong>
        </div>
        <div class="numbers-desc"><strong>Pharma Manufacturers</strong></div>
      </div>
      <div class="div-numbers-grid">
        <div class="numbers-block">
          <span class="counter"><strong>11</strong></span><strong>+</strong>
        </div>
        <div class="numbers-desc"><strong>Years of Historical Data</strong></div>
      </div>
    </div>
  </div>
</section>
<section class="section-home-research">
  <div class="feature-content">
    <div class="sub-section-header">Research</div>
    <div class="spacer-16"></div>
    <h2 class="heading">
      <span class="section-heading-1 _2">Use healthcare data to run effective finance models.</span>
    </h2>
  </div>
  <div class="grid-research">
    <div class="header"></div>
    <div class="columns-4">
      <div class="column-20">
        <div data-w-id="71cbb3ed-cba2-c937-4d02-45c48e7abe52" class="grid-item-research">
          <div class="image-full-fill">
            <img
              class="image-grid-1"
              src="/webflow/images/workflow.png"
              alt=""
              sizes="(max-width: 1024px) 100vw, 1024px"
              loading="lazy"
              srcset="/webflow/images/workflow-p-500.png 500w, /webflow/images/workflow-p-800.png 800w, /webflow/images/workflow.png 1024w"
            />
          </div>
          <div class="card-info">
            <div><strong class="grid-item-value">SEAMLESS</strong></div>
            <div class="grid-item-title"><strong>Integrates with your workflow</strong></div>
            <div class="card-description-wrapper">
              <div class="grid-body-text">
                Our data experts work with you closely, delivering case study analytics that sync
                effortlessly with your existing setup
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="column-20">
        <div data-w-id="71cbb3ed-cba2-c937-4d02-45c48e7abe6d" class="grid-item-research">
          <div class="image-full-fill">
            <img
              class="image-grid-1"
              src="/webflow/images/databrick.png"
              alt=""
              sizes="(max-width: 1024px) 100vw, 1024px"
              loading="lazy"
              srcset="/webflow/images/databrick-p-500.png 500w, /webflow/images/databrick-p-800.png 800w, /webflow/images/databrick.png 1024w"
            />
          </div>
          <div class="card-info">
            <div><strong class="grid-item-value">Scalable</strong></div>
            <div class="grid-item-title"><strong>Capital efficient</strong></div>
            <div class="card-description-wrapper">
              <div class="grid-body-text">
                Large datasets coupled with expert analysis at cost-effective prices
              </div>
            </div>
          </div>
        </div>
        <div data-w-id="71cbb3ed-cba2-c937-4d02-45c48e7abe79" class="grid-item-research">
          <div class="image-full-fill">
            <img
              class="image-grid-1"
              src="/webflow/images/sheet_cluster.png"
              alt=""
              sizes="(max-width: 1024px) 100vw, 1024px"
              loading="lazy"
              srcset="/webflow/images/sheet_cluster-p-500.png 500w, /webflow/images/sheet_cluster-p-800.png 800w, /webflow/images/sheet_cluster.png 1024w"
            />
          </div>
          <div class="card-info">
            <div><strong class="grid-item-value">Speed</strong></div>
            <div class="grid-item-title"><strong>Daily, transparent delivery</strong></div>
            <div class="card-description-wrapper">
              <div class="grid-body-text">
                Delays are costly. Realize unbiased insights months ahead of the competition
              </div>
            </div>
          </div>
        </div>
        <div data-w-id="71cbb3ed-cba2-c937-4d02-45c48e7abe85" class="grid-item-research">
          <div class="image-full-fill">
            <img
              class="image-grid-1"
              src="/webflow/images/symbol_funnel.png"
              alt=""
              sizes="(max-width: 1024px) 100vw, 1024px"
              loading="lazy"
              srcset="/webflow/images/symbol_funnel-p-500.png 500w, /webflow/images/symbol_funnel-p-800.png 800w, /webflow/images/symbol_funnel.png 1024w"
            />
          </div>
          <div class="card-info">
            <div><strong class="grid-item-value">Limitless</strong></div>
            <div class="grid-item-title"><strong>Build models big and small</strong></div>
            <div class="card-description-wrapper">
              <div class="grid-body-text">
                Don’t get stuck in noisy data. Clear inputs for solutions from bespoke to large
                scale
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
<section class="section-home-research">
  <div class="feature-content">
    <a href="/studies" class="sub-section-header hover:opacity-80">CASE STUDIES</a>
    <div class="spacer-16"></div>
    <h2 class="heading"><span class="section-heading-1 _2">Real data. Real insights.</span></h2>
  </div>
  <div class="research-stack home-studies-stack">
    <div class="news-section"></div>
    <div class="grid-2-x-2">
      <div class="_2-columns-5 home-studies-grid">
        <CaseStudyCard
          imageSrc="/screenshots/imx_glp1.svg"
          imageAlt="GLP-1 Drug Market Analysis"
          imageClass="image-7 glp1-card-img"
          title="GLP-1 Drug Market Analysis"
          description="Quarterly claims data reveals the explosive growth of GLP-1 drugs — who's prescribing, who's paying, and where the market is heading."
          href="/glp1"
        />
        <CaseStudyCard
          imageSrc="/screenshots/imx_adc.svg"
          imageAlt="ADC Drug Market Analysis"
          imageClass="image-7 adc-card-img"
          title="ADC Drug Market Analysis"
          description="Quarterly claims data reveals the explosive growth of ADC Oncology drugs — who's prescribing, who's paying, and where the market is heading."
          href="/adc-oncology"
        />
        <CaseStudyCard
          imageSrc="/screenshots/imx_sglt2.svg"
          imageAlt="SGLT-2 Inhibitor Market Analysis"
          imageClass="image-7 sglt2-card-img"
          title="SGLT-2 Inhibitor Market Analysis"
          description="From diabetes drug to cardiovascular cornerstone — IMX Data tracks 10 years of SGLT-2i claim volume, market share, payer friction, and indication shifts."
          href="/studies/sglt-2"
        />
        <CaseStudyCard
          imageSrc="/screenshots/imx_antivegf.svg"
          imageAlt="Next-Gen Anti-VEGF: Market & Claims Analysis"
          imageClass="image-7 antivegf-card-img"
          title="Next-Gen Anti-VEGF: Market & Claims Analysis"
          description="8 years of retinal-injection claims track the shift from off-label Avastin and Eylea toward durable next-gen agents — agent mix, real-world dosing intervals, site of care, and payer."
          href="/studies/anti-vegf"
        />
        <CaseStudyCard
          imageSrc="/screenshots/imx_antiil23.svg"
          imageAlt="Anti-IL-23: The Dual-Benefit Claims Analysis"
          imageClass="image-7 antiil23-card-img"
          title="Anti-IL-23: The Dual-Benefit Claims Analysis"
          description="Pharmacy and medical claims linked on the same patient track the IL-23 class from the p40 originator to p19-selective agents and the first oral — benefit mix, real-world dosing intervals, indication mix, prescriber specialty, and post-LOE biosimilar erosion."
          href="/studies/anti-il-23"
        />
      </div>
    </div>
  </div>
</section>

<section class="section-home-insights-2">
  <section class="section-home-quote">
    <div class="feature-content">
      <h1 class="heading-quote">
        <strong class="bold-text-2"
          >"For too long, healthcare data has been scattered and siloed. We’re here to clear the
          data fog and give finance experts a clear path to unlocking its potential. This is the
          vision guiding my team."</strong
        ><br />
      </h1>
      <div class="sub-section-header"><strong>Jim Plante<br /></strong>CEO</div>
    </div>
  </section>
</section>

<section id="faq" class="section-home-insights-2">
  <div class="container-large">
    <div class="mx-auto max-w-3xl pb-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#11a0a5]">
        Frequently Asked Questions
      </p>
      <h2 class="mt-4 text-4xl font-semibold tracking-tight text-white">
        Everything you need to know about IMX Data
      </h2>
      <p class="mt-4 text-lg/8 text-white/90">
        Healthcare claims data built for institutional investors, hedge funds, and quant funds.
        Can't find what you're looking for?
        <a
          href="/contact"
          class="font-medium text-[#7dd3d6] underline decoration-white/30 underline-offset-2 hover:text-[#a5e8ea] hover:decoration-[#a5e8ea]"
          >Contact us.</a
        >
      </p>

      <dl class="mt-12 divide-y divide-white/20">
        {#each faqs as faq, i}
          <div class="py-6">
            <dt>
              <button
                class="flex w-full items-start justify-between text-left"
                onclick={() => toggleFaq(i)}
                aria-expanded={openFaqIndex === i}
              >
                <span class="text-base font-semibold tracking-tight text-white">{faq.question}</span
                >
                <span class="ml-6 flex shrink-0 items-center">
                  {#if openFaqIndex === i}
                    <svg
                      class="h-5 w-5 text-[#11a0a5]"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M4 10a.75.75 0 01.75-.75h10.5a.75.75 0 010 1.5H4.75A.75.75 0 014 10z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  {:else}
                    <svg
                      class="h-5 w-5 text-white/75"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                      />
                    </svg>
                  {/if}
                </span>
              </button>
            </dt>
            {#if openFaqIndex === i}
              <dd class="mt-4 pr-12">
                {#if faq.answer}
                  {#each faq.answer.split('\n\n') as paragraph}
                    <p class="text-white/92 mb-3 text-base/7">{paragraph}</p>
                  {/each}
                {/if}
                {#if faq.bullets}
                  <ul class="mt-2 space-y-3">
                    {#each faq.bullets as bullet}
                      <li class="text-white/92 text-base/7">
                        <span class="font-semibold text-white">{bullet.label}:</span>
                        {' '}{bullet.text}
                      </li>
                    {/each}
                  </ul>
                {/if}
                {#if faq.studyLinks}
                  <p class="mt-4 text-base/7 text-white/75">
                    Read the full studies:
                    {#each faq.studyLinks as link, i}
                      <a
                        href={link.href}
                        class="font-medium text-[#7dd3d6] underline decoration-white/30 underline-offset-2 hover:text-[#a5e8ea] hover:decoration-[#a5e8ea]"
                        >{link.label}</a
                      >{i < faq.studyLinks.length - 1 ? ' · ' : ''}
                    {/each}
                  </p>
                {/if}
              </dd>
            {/if}
          </div>
        {/each}
      </dl>
    </div>
  </div>
</section>

<section class="section-home-insights-2">
  <div class="heading-section---special">
    <div
      id="w-node-_5e6fbf22-6161-be36-dfcd-f7d307873db1-3b483218"
      class="w-layout-layout stack-spec wf-layout-layout"
    >
      <div class="w-layout-cell">
        <div class="text-box _900px">
          <p class="paragraph extra-large"><strong>SOC 2 Type II Certified</strong></p>
          <div class="text-special-soc-1">
            <strong class="text-special-soc"
              >All data de-identified per HIPAA Safe Harbor (45 CFR 164.514(b)). SOC 2 Type II
              certified in 2025.</strong
            >
            <div class="spacer-16"></div>
            <strong class="text-special-soc"
              >Enterprise-grade security and compliance for institutional clients.</strong
            >
          </div>
          <div class="spacer-32"></div>
        </div>
      </div>
      <div class="w-layout-cell cell-2">
        <img src="/webflow/images/image-8.png" loading="lazy" alt="" class="image-9" />
      </div>
    </div>
  </div>
</section>
<section class="bottom-heading---special is-relative">
  <div class="cta-card-content">
    <div class="text-box">
      <div>
        <h2 class="heading-2">Ready to dive in?</h2>
      </div>
      <div class="spacer-16"></div>
      <div class="paragraph extra-large">
        <strong>Begin designing your data solution today.</strong>
      </div>
    </div>
    <div class="spacer-32"></div>
    <a href="/contact" class="button---cta w-button">Get started</a>
  </div>
  <div class="gradient-image"></div>
</section>
<WebflowFooter />
